import * as React from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { useQuery } from '@tanstack/react-query';
import { Spin, message } from 'antd';
import { pageNavsStore } from '@src/shared/store/navs';
import { generateDefaultTemporaryDashboard, temporaryDashboardDb } from '@shared/utils/dashboard';
import { DashboardProvider } from '../store';
import Dashboard from '../Dashboard';

type AnalysisDrilldownState = Parameters<typeof generateDefaultTemporaryDashboard>[0];

const {
  upsert: upsertNav,
  remove: removeNav,
  find: findNav,
  getNextTempCount,
} = pageNavsStore.getState();

const TempCreate = ({ state }: { state: AnalysisDrilldownState }) => {
  const [loading, setLoading] = React.useState(true);
  const [id, setId] = React.useState<string>();

  React.useEffect(() => {
    void (async () => {
      const id = nanoid(8);
      try {
        await temporaryDashboardDb.setItem(id, {
          id,
          type: 'temp' as const,
          analysis: 'funnel' as const,
          config: generateDefaultTemporaryDashboard(state),
        });
        setId(id);
      } catch {
        message.error('Failed to create a new analysis dashboard.');
      } finally {
        setLoading(false);
      }
    })();
  }, [state]);

  if (loading) return null;

  if (id) return <Navigate replace to={`/funnels/temp/${id}`} />;

  return <Navigate replace to={'/'} />;
};

const TempInit = ({ id }: { id: string }) => {
  const [savedId, setSavedId] = React.useState<string>();
  const query = useQuery({
    queryKey: ['analysis', 'funnel', 'temp', id],
    queryFn: async () => {
      try {
        const dashboard = await temporaryDashboardDb.getItem<AnalysisDashboard<'funnel'>>(id);

        if (!dashboard) {
          removeNav(id);

          throw new Error();
        }

        if (!findNav(id)) {
          upsertNav({
            id,
            type: 'temp',
            name: `Untitled Analysis(${getNextTempCount()})`,
          });
        }

        return dashboard;
      } catch {
        message.error('Dashboard not found, it may have been deleted.');

        return null;
      }
    },
    // Do not cache temporary dashboards in memory, keep to synchronize with IndexedDB on each mount
    cacheTime: 0,
    staleTime: 0,
  });

  // Write back temporary dashboard changes to IndexedDB, don't need to synchronize the state in memory
  const handleChange = React.useCallback<GetProp<typeof DashboardProvider, 'onChange'>>(
    (id, config) =>
      void temporaryDashboardDb.setItem(id, {
        id,
        type: 'temp',
        analysis: 'funnel',
        config,
      }),
    [],
  );

  const handleSave = React.useCallback<GetProp<typeof DashboardProvider, 'onSave'>>(
    (sid) => {
      setSavedId(sid);
      removeNav(id);
    },
    [id],
  );

  if (savedId) return <Navigate replace to={`/funnels/saved/${savedId}`} />;

  if (query.isLoading) {
    return (
      <Spin
        style={{
          height: '75vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      />
    );
  }

  if (query.isError || !query.data) return <Navigate replace to={'/'} />;

  return (
    <DashboardProvider data={query.data} onChange={handleChange} onSave={handleSave}>
      <Dashboard />
    </DashboardProvider>
  );
};

const Temp = () => {
  const { id } = useParams();
  const { state } = useLocation();

  if (id) return <TempInit id={id} />;

  if (state) return <TempCreate state={state} />;

  return <Navigate replace to={'/'} />;
};

export const TempFunnelAnalysis = React.memo(Temp);
